import { api, getAccessToken, type BillingOverview, type BillingInvoice, type SupplierInvoiceList } from '../api';
import { openPdfViewer } from '../dialogs/pdf-viewer';
import { callout, field, input, opt, openDialog, select, todayISO, val } from '../dialog';
import { errorCard, esc, fmtDate, html, loading, pill, tile } from '../ui';
import { bindSupplierInvoices, supplierInvoicesCard } from './supplier-invoices';

/**
 * Billing & invoices.
 *
 * The screen exists to answer one uncomfortable question first: what has been
 * done and not billed. Everything a CAMO charges for is already recorded
 * somewhere — reviews issued, engineering hours signed off, chargeables raised
 * against work packs — so an invoice is drafted from those rather than typed,
 * and unbilled work is shown before the invoices are.
 */

const STATUS_STATE: Record<string, 'OK' | 'DUE_SOON' | 'OVERDUE' | 'NA'> = {
  DRAFT: 'NA',
  SENT: 'DUE_SOON',
  PAID: 'OK',
  OVERDUE: 'OVERDUE',
  CANCELLED: 'NA',
};

const money = (n: number, currency: string) =>
  `${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

function invoiceCard(i: BillingInvoice): string {
  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>${esc(i.reference)} · ${esc(i.customerName)}</h2>
          <p class="tiny dim">
            ${i.periodStart ? `${esc(fmtDate(i.periodStart))} – ${esc(fmtDate(i.periodEnd))}` : 'no period'}
            ${i.dueAt ? ` · due ${esc(fmtDate(i.dueAt))}` : ''}
            ${i.paidAt ? ` · paid ${esc(fmtDate(i.paidAt))}` : ''}
          </p>
        </div>
        <div style="display:flex;gap:8px;align-items:center">
          ${pill(STATUS_STATE[i.status] ?? 'NA', i.status.toLowerCase())}
          <button class="btn sm" data-pdf="${esc(i.id)}">PDF</button>
          ${i.storedStatus === 'DRAFT' ? `<button class="btn sm" data-send="${esc(i.id)}">Mark sent</button>` : ''}
          ${i.storedStatus !== 'PAID' && i.storedStatus !== 'CANCELLED'
            ? `<button class="btn sm" data-paid="${esc(i.id)}">Mark paid</button>`
            : ''}
        </div>
      </div>

      <div class="scrollx">
        <table class="tbl" style="min-width:0">
          <thead><tr><th class="wide">Line</th><th class="n">Qty</th><th class="n">Unit</th><th class="n">Net</th></tr></thead>
          <tbody>
            ${i.lines
              .map(
                (l) => html`<tr>
                  <td class="wide">
                    ${esc(l.description)}
                    ${l.subLabel ? `<span class="sub2">${esc(l.subLabel)}</span>` : ''}
                  </td>
                  <td class="n">${l.quantity}</td>
                  <td class="n">${money(l.unitPrice, i.currency)}</td>
                  <td class="n"><b>${money(l.netAmount, i.currency)}</b></td>
                </tr>`,
              )
              .join('')}
          </tbody>
        </table>
      </div>

      <div class="card-f">
        Net ${esc(money(i.netTotal, i.currency))}${i.vatTotal ? ` · VAT ${esc(money(i.vatTotal, i.currency))}` : ''} ·
        <b>Total ${esc(money(i.grossTotal, i.currency))}</b>${i.paymentRef ? ` · ${esc(i.paymentRef)}` : ''}
      </div>
    </div>
  `;
}

function view(o: BillingOverview, received: SupplierInvoiceList | null): string {
  const unbilledByCustomer = new Map<string, { name: string; items: typeof o.unbilled; total: number }>();
  for (const u of o.unbilled) {
    const g = unbilledByCustomer.get(u.customerId) ?? { name: u.customerName, items: [], total: 0 };
    g.items.push(u);
    g.total += u.netAmount;
    unbilledByCustomer.set(u.customerId, g);
  }

  return html`
    <div class="page-h">
      <div>
        <h1>Billing &amp; invoices</h1>
        <p class="dim">Management retainers and the work actually done, drafted from what is already recorded</p>
      </div>
      <button class="btn pri" data-draft>Draft invoice</button>
    </div>

    <div class="tiles">
      ${tile({
        label: 'Outstanding',
        value: money(o.summary.outstanding, o.currency),
        sub: 'issued and unpaid',
      })}
      ${tile({
        label: 'Overdue',
        value: o.summary.overdue,
        sub: o.summary.overdue ? money(o.summary.overdueValue, o.currency) : 'nothing past due',
        tone: o.summary.overdue ? 'exceed' : 'ok',
      })}
      ${tile({ label: 'Drafts', value: o.summary.draft, sub: 'not yet sent' })}
      ${tile({
        label: 'Unbilled work',
        value: money(o.summary.unbilledValue, o.currency),
        sub: `${o.summary.unbilledItems} item(s) recorded, not invoiced`,
        tone: o.summary.unbilledItems ? 'caution' : 'ok',
      })}
    </div>

    ${exchangeCard(o)}

    ${supplierInvoicesCard(received)}

    ${unbilledByCustomer.size
      ? html`
          <div class="card">
            <div class="card-h">
              <div>
                <h2>Done but not billed</h2>
                <p class="tiny dim">
                  Chargeables recorded against work packs and reviews that have not reached an invoice
                </p>
              </div>
              ${pill('DUE_SOON', money(o.summary.unbilledValue, o.currency))}
            </div>
            <div class="rows">
              ${[...unbilledByCustomer.entries()]
                .map(
                  ([id, g]) => html`
                    <div class="row" style="border-left-color:var(--caution)">
                      <div class="bd">
                        <div class="t">${esc(g.name)} — ${esc(money(g.total, o.currency))}</div>
                        <div class="m">
                          ${esc(g.items.slice(0, 3).map((i) => `${i.description}${i.registration ? ` (${i.registration})` : ''}`).join('; '))}
                          ${g.items.length > 3 ? ` and ${g.items.length - 3} more` : ''}
                        </div>
                      </div>
                      <button class="btn sm" data-draft-for="${esc(id)}">Draft invoice</button>
                    </div>
                  `,
                )
                .join('')}
            </div>
            <div class="card-f">
              An invoice is built from these, so nothing billable is invented and nothing done goes unbilled.
              Each chargeable is attached to its line when the invoice is drafted, so it cannot be billed twice.
            </div>
          </div>
        `
      : callout('ok', '✓', html`Everything recorded as chargeable has been invoiced.`)}

    <div class="stack">
      ${o.invoices.length
        ? o.invoices.map(invoiceCard).join('')
        : html`<div class="card"><div class="card-b muted">No invoices yet.</div></div>`}
    </div>
  `;
}

// ── dialogs ─────────────────────────────────────────────────────────────────

function monthStart(): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
}

/**
 * What the work is priced in — the rate card, not the organisation.
 *
 * The distinction is the whole point: a Romanian operator prices engineering
 * hours in EUR and raises the invoice in RON.
 */
const pricedIn = (o: BillingOverview) => o.rateCard?.currency ?? o.exchange?.base ?? o.currency;

/** What a new invoice is raised in unless somebody says otherwise. */
const defaultInvoiceCurrency = (o: BillingOverview) => o.exchange?.quote ?? o.currency;

function draftInvoice(o: BillingOverview, customerId: string | null, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Draft invoice',
    sub: 'Built from the chargeables already recorded for the period',
    primary: 'Draft it',
    body: () => html`
      <div class="frow3">
        ${field('Customer', select('customerId', o.customers.map((c) => opt(c.id, `${c.name}${c.aircraftCount ? ` — ${c.aircraftCount} aircraft` : ''}`, c.id === customerId)).join('')))}
        ${field('Period from', input('periodStart', monthStart(), 'type="date"'))}
        ${field('Period to', input('periodEnd', todayISO(), 'type="date"'))}
      </div>
      <div class="frow">
        ${field('Due', input('dueAt', '', 'type="date"'), 'Leave blank to use the customer’s payment terms')}
        ${field(
          'Raise it in',
          select('currency', ['RON', 'EUR', 'USD', 'GBP', 'CHF']
            .map((c) => opt(c, c, c === defaultInvoiceCurrency(o)))
            .join(''), 'data-invoice-currency'),
          pricedIn(o) === defaultInvoiceCurrency(o)
            ? 'Work is priced in the same currency — nothing is converted'
            : `Work is priced in ${pricedIn(o)}, so anything else is converted at the rate on the billing screen`,
        )}
      </div>
      ${field('Notes', input('notes', '', 'maxlength="4000"'))}

      ${/* Said before the attempt, not after the refusal. Choosing a currency
            with no rate behind it is the one way this dialog fails, and the
            person choosing is the person who can fix it. */ ''}
      <div data-rate-note></div>

      ${callout('info', 'i', html`
        Only unbilled chargeables in that period are picked up, and each is attached to its invoice line —
        so the same work cannot appear on a second invoice. If nothing is unbilled, there is nothing to
        draft and the request is refused rather than producing an empty invoice.
      `)}
    `,
    onRender: (root) => {
      const currency = root.querySelector<HTMLSelectElement>('[data-invoice-currency]');
      const note = root.querySelector<HTMLElement>('[data-rate-note]');
      if (!currency || !note) return;

      const priced = pricedIn(o);
      const x = o.exchange;
      const describe = () => {
        const chosen = currency.value;
        if (chosen === priced) {
          note.innerHTML = '';
          return;
        }
        const usable = x && x.base === priced && x.quote === chosen && x.rate !== null;
        note.innerHTML = usable
          ? callout('info', 'i', html`
              Converted at <b>1 ${esc(priced)} = ${x!.rate!.toFixed(4)} ${esc(chosen)}</b>,
              the rate from ${esc(fmtDate(x!.asOf ?? ''))}. The invoice keeps that figure.
            `)
          : callout('caution', '!', html`
              There is no <b>${esc(priced)} → ${esc(chosen)}</b> rate set, so this cannot be drafted in
              ${esc(chosen)}. Set one on this screen first, or raise it in ${esc(priced)}.
            `);
      };
      currency.addEventListener('change', describe);
      describe();
    },
    collect: (root) => {
      const periodStart = val(root, 'periodStart');
      const periodEnd = val(root, 'periodEnd');
      if (!periodStart || !periodEnd) return { error: 'Give the period the invoice covers.' };
      if (periodEnd < periodStart) return { error: 'The period ends before it starts.' };
      return {
        customerId: val(root, 'customerId'),
        periodStart,
        periodEnd,
        dueAt: val(root, 'dueAt') || undefined,
        notes: val(root, 'notes') || undefined,
        currency: val(root, 'currency') || undefined,
      };
    },
    submit: (payload) => api.billing.draft(payload),
    done: (r) => `${r.reference} drafted — ${r.lines} line(s), ${r.grossTotal.toFixed(2)}`,
    onDone: reload,
  });
}

function markPaid(invoice: BillingInvoice, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Mark paid',
    sub: `${invoice.reference} — ${invoice.customerName}`,
    primary: 'Mark paid',
    body: () => html`
      <div class="frow">
        ${field('Paid on', input('paidAt', todayISO(), 'type="date"'))}
        ${field('Payment reference', input('paymentRef', '', 'maxlength="80"'))}
      </div>
    `,
    collect: (root) => ({
      paidAt: val(root, 'paidAt') || undefined,
      paymentRef: val(root, 'paymentRef') || undefined,
    }),
    submit: (payload) => api.billing.markPaid(invoice.id, payload),
    done: (r) => `${r.reference} marked paid`,
    onDone: reload,
  });
}

/**
 * What a currency is worth, and when that was decided.
 *
 * Sits under the tiles because it belongs to the money rather than to any one
 * invoice, and because a missing rate has to be visible *before* somebody
 * tries to raise an invoice that needs one — finding out at the point of
 * issuing is finding out too late.
 *
 * The date is shown as prominently as the figure. A rate without a date is not
 * a fact about anything, and in Romania an invoice in a foreign currency has to
 * carry the conversion that was actually applied on the day.
 */
function exchangeCard(o: BillingOverview): string {
  const x = o.exchange;
  if (!x) return '';

  const sameCurrency = x.base === x.quote;

  return html`
    <div class="card" data-exchange>
      <div class="card-h">
        <div>
          <h2>Exchange rate</h2>
          <p class="tiny dim">
            ${sameCurrency
              ? `Everything is priced and invoiced in ${esc(x.quote)} — nothing to convert.`
              : `Used when work priced in ${esc(x.base)} is invoiced in ${esc(x.quote)}. An invoice keeps
                 the rate it was raised at, so setting a new one never changes a document already issued.`}
          </p>
        </div>
        ${sameCurrency
          ? ''
          : x.rate === null
            ? pill('OVERDUE', 'not set')
            : pill('OK', `1 ${x.base} = ${x.rate.toFixed(4)} ${x.quote}`)}
      </div>

      ${sameCurrency
        ? ''
        : html`
            <div class="card-b">
              ${x.rate === null
                ? callout('caution', '!', html`
                    <b>No rate has been set.</b> An invoice in ${esc(x.quote)} for work priced in
                    ${esc(x.base)} cannot be raised until there is one — it would have to guess, and a
                    guess here is wrong by a factor of five.
                  `)
                : html`
                    <div class="rows">
                      <div class="row">
                        <div class="bd">
                          ${/* The figure itself is in the pill above — repeating it here
                                just made the reader check whether the two agreed. */ ''}
                          <div class="t">Applies from ${esc(fmtDate(x.asOf ?? ''))}</div>
                          <div class="m">
                            ${x.source ? `${esc(x.source)} · ` : ''}the next document drawn up will use this
                          </div>
                        </div>
                        <button class="btn sm" data-set-rate>Change it</button>
                      </div>
                    </div>
                  `}
              ${x.rate === null
                ? html`<div style="margin-top:12px"><button class="btn pri" data-set-rate>Set the rate</button></div>`
                : ''}

              ${x.history.length > 1
                ? html`
                    <details class="card-f" style="cursor:pointer">
                      <summary><b>Earlier rates</b></summary>
                      <div class="scrollx" style="margin-top:10px;cursor:default">
                        <table class="tbl">
                          <thead><tr><th>From</th><th>Pair</th><th>Rate</th><th>Source</th></tr></thead>
                          <tbody>
                            ${x.history.map((h) => html`
                              <tr>
                                <td class="tiny">${esc(fmtDate(h.asOf))}</td>
                                <td class="tiny">${esc(h.baseCurrency)} → ${esc(h.quoteCurrency)}</td>
                                <td class="tiny" style="font-family:var(--mono)">${h.rate.toFixed(4)}</td>
                                <td class="tiny dim">${esc(h.source ?? '—')}</td>
                              </tr>
                            `).join('')}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  `
                : ''}
            </div>
          `}
    </div>
  `;
}

/** Setting it. Defaults to today, because that is nearly always the answer. */
function setRateDialog(o: BillingOverview, reload: () => void): void {
  const x = o.exchange!;

  openDialog<{ rate: number; asOf: string; source?: string }>({
    title: 'Exchange rate',
    sub: `${x.base} to ${x.quote}`,
    primary: 'Save it',
    body: () => html`
      <div class="frow">
        ${field(`1 ${x.base} in ${x.quote}`, input('rate', x.rate === null ? '' : String(x.rate),
          'inputmode="decimal" style="text-align:right;font-family:var(--mono)" placeholder="4.9772"'))}
        ${field('From', input('asOf', todayISO(), 'type="date"'), 'The day it applies from')}
      </div>
      ${field('Where it came from', input('source', x.source ?? 'BNR', 'maxlength="80" placeholder="BNR"'),
        'Not checked — it is here so somebody can say why this figure and not another')}
      ${callout('info', 'i', html`
        Invoices already raised keep the rate they were raised at. This is the figure the
        <b>next</b> document will use.
      `)}
    `,
    collect: (root) => {
      // A comma is what a Romanian keyboard produces, and "4,9772" parses as NaN.
      const rate = Number(val(root, 'rate').replace(',', '.'));
      if (!(rate > 0)) return { error: 'Give a rate greater than zero.' };

      /**
       * Judged against the rate already set, not against a fixed ceiling.
       *
       * The mistake to catch is a decimal point one place out — 49.772 for
       * 4.9772. A ceiling cannot see that: the first version of this refused
       * anything above 1000, which let 49.772 through and stored it. The API
       * refuses beyond a factor of five; this warns earlier, because a fifth
       * of a move is already worth a second look and stopping at the server is
       * a worse place to find out.
       */
      if (x.rate !== null) {
        const factor = rate / x.rate;
        if (factor >= 5 || factor <= 0.2) {
          return {
            error: `That is ${factor >= 5 ? `${factor.toFixed(1)}×` : `${(1 / factor).toFixed(1)}× smaller than`} ` +
              `the current ${x.rate}. Check the decimal point.`,
          };
        }
      }
      return { rate, asOf: val(root, 'asOf') || todayISO(), source: val(root, 'source') || undefined };
    },
    submit: (p) => api.billing.setExchangeRate({ base: x.base, quote: x.quote, ...p }),
    done: (r: { rate: number | null; base: string; quote: string }) =>
      `1 ${r.base} = ${r.rate} ${r.quote}`,
    onDone: reload,
  });
}

export async function renderBilling(host: HTMLElement): Promise<void> {
  host.innerHTML = loading('Loading billing');
  try {
    const [o, received] = await Promise.all([
      api.billing.overview(),
      // Absent rather than fatal: an engineer reading what a job was billed at
      // should still get the page if the received-invoice call fails.
      api.billing.supplierInvoices().catch(() => null),
    ]);
    host.innerHTML = view(o, received);
    const reload = () => void renderBilling(host);

    bindSupplierInvoices(host, o.exchange, reload);

    host.querySelectorAll<HTMLElement>('[data-set-rate]').forEach((b) =>
      b.addEventListener('click', () => setRateDialog(o, reload)),
    );

    host.querySelector('[data-draft]')?.addEventListener('click', () =>
      draftInvoice(o, o.customers[0]?.id ?? null, reload),
    );
    host.querySelectorAll<HTMLElement>('[data-draft-for]').forEach((b) =>
      b.addEventListener('click', () => draftInvoice(o, b.dataset.draftFor!, reload)),
    );
    host.querySelectorAll<HTMLElement>('[data-send]').forEach((b) =>
      b.addEventListener('click', async () => {
        await api.billing.markSent(b.dataset.send!);
        reload();
      }),
    );
    host.querySelectorAll<HTMLElement>('[data-pdf]').forEach((b) =>
      b.addEventListener('click', () => {
        const i = o.invoices.find((x) => x.id === b.dataset.pdf);
        if (!i) return;
        openPdfViewer({
          path: `/billing/invoices/${i.id}/pdf`,
          title: i.reference,
          subtitle: `${i.customerName} · ${money(i.grossTotal, i.currency)}${
            i.storedStatus === 'DRAFT' ? ' · draft' : ''
          }`,
          accessToken: getAccessToken,
        });
      }),
    );
    host.querySelectorAll<HTMLElement>('[data-paid]').forEach((b) =>
      b.addEventListener('click', () => {
        const i = o.invoices.find((x) => x.id === b.dataset.paid);
        if (i) markPaid(i, reload);
      }),
    );
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load billing.');
  }
}
